import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Container } from '@mui/material';
import authService from '../services/auth';

/**
 * Receives ?code= from Google OAuth redirect (single-use exchange code).
 * Never expects a JWT in the URL.
 */
const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Legacy hash links are rewritten before render; keep a fallback if one remains.
    const hash = window.location.hash;
    const qIndex = hash.indexOf('?');
    const hashParams = qIndex >= 0 ? new URLSearchParams(hash.slice(qIndex + 1)) : null;
    const code = params.get('code') || hashParams?.get('code');

    if (!code) {
      setError('Missing exchange code.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authService.exchangeOAuthCode(code);
        if (cancelled) return;
        authService.setToken(response.token, true);
        navigate('/dashboard', { replace: true });
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message || 'Could not complete Google sign-in.';
        setError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {error ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Completing sign-in…</Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default OAuthCallback;
